import { useEffect, useMemo, useRef } from 'preact/hooks';
import { THREE, OrbitControls } from '../../../lib/three-client.js';
import {
  createElectricLine,
  createInnerParcelOutline,
  createParcelMesh,
  createParcelOutline,
  createSideWalls,
  fitCameraToObject,
} from './createMorphologyMesh.js';

function supportsWebGL() {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl') || c.getContext('experimental-webgl'));
  } catch {
    return false;
  }
}

function meshDataFingerprint(data) {
  if (!data || typeof data !== 'object') return '';
  const mesh = data.mesh || {};
  const verts = Array.isArray(mesh.vertices) ? mesh.vertices.length : 0;
  const faces = Array.isArray(mesh.faces) ? mesh.faces.length : (Array.isArray(mesh.triangles) ? mesh.triangles.length : 0);
  const sid = data.snapshot_id ?? data.parcel?.ada ?? data.parcel?.parsel ?? '';
  const gen = data.generated_at || data.built_at || '';
  const typ = data.morphology?.type ?? data.stats?.morphology_label ?? '';
  return `${sid}:${verts}:${faces}:${gen}:${typ}`;
}

export default function ParcelMorphologyThreeScene({
  data,
  onUnsupported,
  onReady = null,
  compact = false,
}) {
  const hostRef = useRef(null);
  const onUnsupportedRef = useRef(onUnsupported);
  const onReadyRef = useRef(onReady);
  const dataRef = useRef(data);
  onUnsupportedRef.current = onUnsupported;
  onReadyRef.current = onReady;
  dataRef.current = data;

  const fingerprint = useMemo(() => meshDataFingerprint(data), [data]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !fingerprint) return undefined;

    if (!supportsWebGL()) {
      onUnsupportedRef.current?.();
      return undefined;
    }

    const sceneData = dataRef.current;
    const width = Math.max(host.clientWidth, compact ? 160 : 280);
    const height = Math.max(host.clientHeight, compact ? 160 : 240);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8fafc);

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1000, 1000);
    camera.position.set(80, 90, -110);
    camera.up.set(0, 1, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: !compact,
      alpha: false,
      powerPreference: 'low-power',
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, compact ? 1.25 : 2));
    renderer.setSize(width, height, false);
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    host.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.72);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.85);
    dir.position.set(80, 140, -60);
    scene.add(dir);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minZoom = 0.55;
    controls.maxZoom = 4.5;
    controls.maxPolarAngle = Math.PI * 0.48;
    controls.minPolarAngle = Math.PI * 0.12;

    const root = new THREE.Group();
    root.name = 'morphologyRoot';
    scene.add(root);

    const surface = createParcelMesh(sceneData);
    if (surface) root.add(surface);
    const walls = createSideWalls(sceneData, surface);
    if (walls) root.add(walls);
    const bufferOutline = createParcelOutline(sceneData, surface);
    if (bufferOutline) root.add(bufferOutline);
    const inner = createInnerParcelOutline(sceneData, surface);
    if (inner) root.add(inner);
    const electric = createElectricLine(sceneData, surface);
    if (electric) root.add(electric);

    fitCameraToObject(camera, root, controls, { width, height });

    let raf = 0;
    let frame = 0;
    let visible = true;
    let readySent = false;

    const tick = () => {
      if (!visible) {
        raf = requestAnimationFrame(tick);
        return;
      }
      controls.update();
      renderer.render(scene, camera);
      frame += 1;
      if (!readySent && frame >= 2) {
        readySent = true;
        try { onReadyRef.current?.(); } catch {}
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    const onContextLost = (event) => {
      event.preventDefault();
    };
    const onContextRestored = () => {
      const w = Math.max(host.clientWidth, compact ? 120 : 200);
      const h = Math.max(host.clientHeight, compact ? 120 : 180);
      renderer.setSize(w, h, false);
      renderer.render(scene, camera);
    };
    renderer.domElement.addEventListener('webglcontextlost', onContextLost, false);
    renderer.domElement.addEventListener('webglcontextrestored', onContextRestored, false);

    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (!rect || rect.width < 2 || rect.height < 2) return;
        const w = Math.max(Math.round(rect.width), compact ? 120 : 200);
        const h = Math.max(Math.round(rect.height), compact ? 120 : 180);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(w, h, false);
        fitCameraToObject(camera, root, controls, { width: w, height: h });
        renderer.render(scene, camera);
      })
      : null;
    if (ro) ro.observe(host);

    let io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => {
        const hit = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0);
        visible = hit;
        if (!hit) return;
        const w = Math.max(host.clientWidth, compact ? 120 : 200);
        const h = Math.max(host.clientHeight, compact ? 120 : 180);
        if (w < 2 || h < 2) return;
        renderer.setSize(w, h, false);
        renderer.render(scene, camera);
      }, { root: null, threshold: [0, 0.01, 0.1] });
      io.observe(host);
    }

    const onVisibility = () => {
      if (document.hidden) return;
      renderer.render(scene, camera);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      io?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', onContextRestored);
      controls.dispose();
      renderer.dispose();
      root.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
      if (renderer.domElement.parentNode === host) {
        host.removeChild(renderer.domElement);
      }
    };
  }, [fingerprint, compact]);

  return <div ref={hostRef} className="pp-pcm-three__canvas-host" />;
}
